/** The parts of the postal address of a project that the projects can be filtered by */
export const LOCATION_PARTS = ["city", "country"];

/** A part of the postal address of a project that the projects can be filtered by */

/** The ID of the filter for the projects without a location */
export const NO_LOCATION_FILTER_ID = "noLocation";

/** The texts of each part of the location, and the IDs of its filter for projects without one */
export const LOCATION_PART_LABELS = {
  city: {
    name: "City",
    pluralName: "cities",
    noneLabel: "No city",
    noneFilterId: "noCity"
  },
  country: {
    name: "Country",
    pluralName: "countries",
    noneLabel: "No country",
    noneFilterId: "noCountry"
  }
};

/** Separates the part from the name in the ID of a filter for a city or country, e.g. "city:Berlin" */
const LOCATION_NAME_SEPARATOR = ":";

/** A city or country the projects are filtered by */

/**
 * Returns the filter options for the projects without a location, city or country. A project has no
 * location when it has no coordinates, or they are 0,0, which projects were once given for none.
 */
export function getProjectsLocationFilterPresets() {
  return {
    [NO_LOCATION_FILTER_ID]: {
      id: NO_LOCATION_FILTER_ID,
      label: "No location",
      hasEntityPassedFilter: project => !hasProjectCoordinates(project)
    },
    ...Object.fromEntries(LOCATION_PARTS.map(part => {
      const {
        noneLabel,
        noneFilterId
      } = LOCATION_PART_LABELS[part];
      return [noneFilterId, {
        id: noneFilterId,
        label: noneLabel,
        hasEntityPassedFilter: project => getProjectLocationName(project, part) === undefined
      }];
    }))
  };
}

/**
 * Returns the filter options for the cities and countries among the IDs, which match the projects
 * in them. The IDs that are no city or country are left out.
 */
export function getLocationNameFilterOptions(filterIds) {
  return Object.fromEntries(filterIds.flatMap(filterId => {
    const locationName = parseLocationNameFilterId(filterId);
    if (!locationName) {
      return [];
    }
    const {
      part,
      name
    } = locationName;
    return [[filterId, {
      id: filterId,
      label: name,
      hasEntityPassedFilter: project => getProjectLocationName(project, part) === name
    }]];
  }));
}

/** Returns the ID of the filter for the city or country */
export function getLocationNameFilterId(part, name) {
  return `${part}${LOCATION_NAME_SEPARATOR}${name}`;
}

/** Returns the city or country of the ID, or undefined if it is none or has no name */
export function parseLocationNameFilterId(filterId) {
  const separatorIndex = filterId.indexOf(LOCATION_NAME_SEPARATOR);
  const part = filterId.slice(0, separatorIndex);
  const name = filterId.slice(separatorIndex + 1).trim();
  if (separatorIndex < 0 || !isLocationPart(part) || name === "") {
    return undefined;
  }
  return {
    part,
    name
  };
}

/** Returns the cities or countries of the projects, each once and sorted by name */
export function getProjectsLocationNames(projects, part) {
  const names = new Set(projects.flatMap(project => getProjectLocationName(project, part) ?? []));
  return [...names].sort((a, b) => a.localeCompare(b));
}

/** Returns the city or country in the postal address of the project, or undefined if it has none */
function getProjectLocationName(project, part) {
  const name = part === "city" ? project.postalAddress?.locality : project.postalAddress?.country;
  return name?.trim() || undefined;
}

/** Whether the project has coordinates other than 0,0 */
function hasProjectCoordinates({
  position
}) {
  return !!position && (position.lat !== 0 || position.lng !== 0);
}

/** Whether the text is one of the parts of the location the projects can be filtered by */
function isLocationPart(text) {
  return LOCATION_PARTS.some(part => part === text);
}